import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import BonusTutorial from '../components/bonus/BonusTutorial';

// ── Style helpers ──────────────────────────────────────────────

const modalOverlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.5)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000
};

const modalBox = {
  background: 'var(--card-bg)', padding: '24px', borderRadius: '12px',
  maxWidth: '500px', width: '90%', maxHeight: '90vh', overflow: 'auto'
};

const card = {
  backgroundColor: 'var(--card-bg)', borderRadius: '4px',
  padding: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  marginBottom: '0.75rem'
};

const statCard = (color) => ({
  backgroundColor: 'var(--card-bg)', borderLeft: `3px solid ${color}`,
  borderRadius: '4px', padding: '0.5rem 0.75rem',
  textAlign: 'center', minWidth: '120px', flex: '1 1 0',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
});

const tableStyle = {
  width: '100%', borderCollapse: 'collapse', fontSize: '12px'
};

const th = {
  textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--border)',
  color: 'var(--text-muted)', fontWeight: 600, fontSize: '11px', whiteSpace: 'nowrap'
};

const td = {
  padding: '6px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle'
};

const inputStyle = {
  width: '100%', padding: '6px 8px', borderRadius: '4px',
  border: '1px solid var(--border)', background: 'var(--bg)',
  color: 'var(--text)', fontSize: '12px'
};

const selectStyle = { ...inputStyle };

const sectionTitle = {
  fontSize: '13px', fontWeight: 600, color: 'var(--text)',
  marginBottom: '8px', marginTop: '16px'
};

const fmt = (v) => Number(v || 0).toFixed(2);

const progressColor = (pct) => {
  if (pct >= 80) return 'var(--success)';
  if (pct >= 50) return 'var(--warning)';
  return 'var(--danger)';
};

const monthNames = [
  'Styczen', 'Luty', 'Marzec', 'Kwiecien', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpien', 'Wrzesien', 'Pazdziernik', 'Listopad', 'Grudzien'
];

// ── Progress bar ───────────────────────────────────────────────

const ProgressBar = ({ value, max, height = 8, showLabel = false }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ width: '100%' }}>
      <div style={{
        width: '100%', height, borderRadius: height / 2,
        backgroundColor: 'var(--border)', overflow: 'hidden'
      }}>
        <div style={{
          width: `${pct}%`, height: '100%', borderRadius: height / 2,
          backgroundColor: progressColor(pct), transition: 'width 0.3s'
        }} />
      </div>
      {showLabel && (
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px', textAlign: 'right' }}>
          {fmt(value)} / {fmt(max)} ({pct.toFixed(0)}%)
        </div>
      )}
    </div>
  );
};

// ── Month navigation ──────────────────────────────────────────

const MonthNav = ({ rok, miesiac, onChange }) => {
  const prev = () => {
    if (miesiac === 1) onChange(rok - 1, 12);
    else onChange(rok, miesiac - 1);
  };
  const next = () => {
    const now = new Date();
    const currentM = now.getMonth() + 1;
    const currentY = now.getFullYear();
    if (rok > currentY || (rok === currentY && miesiac >= currentM)) return;
    if (miesiac === 12) onChange(rok + 1, 1);
    else onChange(rok, miesiac + 1);
  };
  const now = new Date();
  const isCurrentMonth = rok === now.getFullYear() && miesiac === now.getMonth() + 1;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <button className="btn" style={{ padding: '4px 8px', fontSize: '14px' }} onClick={prev}>&lsaquo;</button>
      <span style={{ fontSize: '13px', fontWeight: 600, minWidth: '140px', textAlign: 'center', color: 'var(--text)' }}>
        {monthNames[miesiac - 1]} {rok}
      </span>
      <button
        className="btn"
        style={{ padding: '4px 8px', fontSize: '14px', opacity: isCurrentMonth ? 0.3 : 1 }}
        onClick={next}
        disabled={isCurrentMonth}
      >
        &rsaquo;
      </button>
    </div>
  );
};

// ── Explanation modal ─────────────────────────────────────────

const ExplanationModal = ({ item, onClose }) => {
  if (!item) return null;

  const marza = Number(item.marza_procent || 0);
  const minMarza = Number(item.min_marza_procent || 0);
  const cenaStd = Number(item.cena_jednostkowa || 0);
  const cenaSprz = Number(item.cena_sprzedazy || 0);
  const cenaZakupu = Number(item.cena_zakupu || 0);
  const rabat = cenaStd > 0 ? ((1 - cenaSprz / cenaStd) * 100) : 0;

  // Estimate: what discount would still give enough margin
  // margin = (price - cost) / price * 100 >= minMarza
  // price >= cost / (1 - minMarza/100)
  const minPrice = cenaZakupu > 0 ? cenaZakupu / (1 - minMarza / 100) : 0;
  const maxRabat = cenaStd > 0 && minPrice > 0 ? Math.max(0, (1 - minPrice / cenaStd) * 100) : 0;
  const okMarza = cenaZakupu > 0 && minPrice > 0 ? ((minPrice - cenaZakupu) / minPrice * 100) : 0;

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px', fontSize: '15px', color: 'var(--text)' }}>
          {item.premia_naliczona ? 'Szczegoly premii' : 'Dlaczego premia nie zostala naliczona?'}
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
            <span style={{ color: 'var(--text-muted)' }}>Paragon:</span>
            <span style={{ fontWeight: 600 }}>{item.numer_paragonu || '-'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
            <span style={{ color: 'var(--text-muted)' }}>Produkt:</span>
            <span style={{ fontWeight: 600 }}>{item.nazwa_produktu || '-'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
            <span style={{ color: 'var(--text-muted)' }}>Cena jednostkowa:</span>
            <span>{fmt(cenaStd)} zl</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
            <span style={{ color: 'var(--text-muted)' }}>Cena sprzedazy:</span>
            <span>{fmt(cenaSprz)} zl {rabat > 0.01 ? `(rabat ${rabat.toFixed(1)}%)` : ''}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
            <span style={{ color: 'var(--text-muted)' }}>Cena zakupu (FIFO):</span>
            <span>{fmt(cenaZakupu)} zl</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
            <span style={{ color: 'var(--text-muted)' }}>Marza:</span>
            <span style={{ fontWeight: 600 }}>{marza.toFixed(1)}%</span>
          </div>
        </div>

        <div style={{
          padding: '10px 12px', borderRadius: '6px',
          backgroundColor: 'rgba(220,53,69,0.08)', color: 'var(--danger)',
          fontSize: '12px', fontWeight: 600, marginBottom: '12px'
        }}>
          &#10060; {item.powod_odrzucenia || (marza < minMarza
            ? `Marza ${marza.toFixed(1)}% jest ponizej wymaganego minimum ${minMarza.toFixed(1)}%`
            : 'Premia nie zostala naliczona')}
        </div>

        {maxRabat > 0.01 && (
          <div style={{
            padding: '10px 12px', borderRadius: '6px',
            backgroundColor: 'rgba(13,110,253,0.06)', color: 'var(--info)',
            fontSize: '12px', marginBottom: '16px'
          }}>
            &#128161; Tip: Przy rabacie do ~{maxRabat.toFixed(0)}% marza wyniosalaby ok. {okMarza.toFixed(1)}% i premia zostalaby naliczona
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" style={{ fontSize: '12px' }} onClick={onClose}>
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════

export default function MyBonusPage() {
  const { user } = useAuth();
  const { locationId } = useLocation();

  const now = new Date();
  const [rok, setRok] = useState(now.getFullYear());
  const [miesiac, setMiesiac] = useState(now.getMonth() + 1);

  const [loading, setLoading] = useState(true);
  const [bonus, setBonus] = useState(null);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);

  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [explanationItem, setExplanationItem] = useState(null);

  // Filters
  const [productFilter, setProductFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const handleMonthChange = useCallback((r, m) => { setRok(r); setMiesiac(m); }, []);

  // ── Fetch data ────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    try {
      const [bonusRes, detailsRes] = await Promise.all([
        api.get('/bonus/my', { params: { location_id: locationId } }),
        api.get('/bonus/my/details', { params: { rok, miesiac, location_id: locationId } })
      ]);
      if (bonusRes.data.success) setBonus(bonusRes.data.data);
      if (detailsRes.data.success) setDetails(detailsRes.data.data);
    } catch (err) {
      console.error('Blad wczytywania premii:', err);
      setError('Nie udalo sie wczytac danych premii.');
      toast.error('Blad wczytywania danych premii');
    } finally {
      setLoading(false);
    }
  }, [rok, miesiac, locationId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Derived data ──────────────────────────────────────────────

  // Obsługa odpowiedzi - może być obiekt lub struktura z lokalizacjami
  const rawBonus = bonus || {};
  const b = rawBonus.lokalizacje && rawBonus.lokalizacje.length > 0 ? rawBonus.lokalizacje[0] : rawBonus;
  const d = details || {};

  const sprzedaz = Number(b.sprzedaz_ogolem || 0);
  const bazowa = Number(b.premia_bazowa || 0);
  const produktowa = Number(b.premia_produktowa || 0);
  const razem = Number(rawBonus.premia_razem ?? b.premia_razem ?? bazowa + produktowa);
  const nextThreshold = Number(b.nastepny_prog?.kwota || 0);
  const nextThresholdBonus = Number(b.nastepny_prog?.premia || 0);
  const dniPrzepracowane = Number(b.faktyczne_dni || 0);
  const dniDeklarowane = Number(b.deklarowane_dni || 0);
  const progEfektywny = Number(b.prog_efektywny || 0);

  const allItems = d.pozycje || [];

  // Product list for filter dropdown
  const productNames = useMemo(() => {
    const names = new Set();
    allItems.forEach((item) => {
      const n = item.nazwa_produktu || '';
      if (n) names.add(n);
    });
    return Array.from(names).sort();
  }, [allItems]);

  // Filtered items
  const filteredItems = useMemo(() => {
    return allItems.filter((item) => {
      const name = item.nazwa_produktu || '';
      const paragon = item.numer_paragonu || '';
      if (productFilter && name !== productFilter) return false;
      if (statusFilter === 'problems' && item.premia_naliczona !== false) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!name.toLowerCase().includes(q) && !paragon.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [allItems, productFilter, statusFilter, searchQuery]);

  // Summary per product
  const productSummary = useMemo(() => {
    const map = {};
    allItems.forEach((item) => {
      const name = item.nazwa_produktu || 'Nieznany';
      if (!map[name]) map[name] = { nazwa: name, sprzedane: 0, ok: 0, pominiete: 0, sumMarza: 0, countMarza: 0, premia: 0 };
      const qty = Number(item.ilosc || 1);
      map[name].sprzedane += qty;
      if (item.premia_naliczona === false) {
        map[name].pominiete += qty;
      } else {
        map[name].ok += qty;
        map[name].premia += Number(item.premia_kwota || 0);
      }
      const m = Number(item.marza_procent || 0);
      if (m > 0) { map[name].sumMarza += m; map[name].countMarza += 1; }
    });
    return Object.values(map).sort((a, b) => b.premia - a.premia);
  }, [allItems]);

  const totalSummary = useMemo(() => {
    return productSummary.reduce((acc, p) => ({
      sprzedane: acc.sprzedane + p.sprzedane,
      ok: acc.ok + p.ok,
      pominiete: acc.pominiete + p.pominiete,
      premia: acc.premia + p.premia
    }), { sprzedane: 0, ok: 0, pominiete: 0, premia: 0 });
  }, [productSummary]);

  // ── Loading / Error states ────────────────────────────────────

  if (loading) {
    return (
      <div style={{ padding: '0.75rem' }}>
        <div style={{ ...card, textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
          Ladowanie danych premii...
        </div>
      </div>
    );
  }

  if (error && !bonus) {
    return (
      <div style={{ padding: '0.75rem' }}>
        <div style={{ ...card, textAlign: 'center', padding: '2rem', color: 'var(--danger)' }}>
          {error}
          <div style={{ marginTop: '8px' }}>
            <button className="btn btn-primary" style={{ fontSize: '12px' }} onClick={fetchData}>
              Sprobuj ponownie
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0.75rem', fontSize: '12px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
        <h4 style={{ margin: 0, fontSize: '16px', color: 'var(--text)' }}>
          Moja premia &mdash; {monthNames[miesiac - 1]} {rok}
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            className="btn"
            style={{ fontSize: '11px', color: 'var(--text-muted)' }}
            onClick={() => setTutorialOpen(true)}
          >
            &#10067; Jak to dziala?
          </button>
          <MonthNav rok={rok} miesiac={miesiac} onChange={handleMonthChange} />
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <div style={statCard('var(--info)')}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Sprzedaz</div>
          <div style={{ fontSize: '18px', fontWeight: 700 }}>{fmt(sprzedaz)} zl</div>
        </div>
        <div style={statCard('var(--success)')}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Premia bazowa</div>
          <div style={{ fontSize: '18px', fontWeight: 700 }}>{fmt(bazowa)} zl</div>
        </div>
        <div style={statCard('var(--purple)')}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Premia produktowa</div>
          <div style={{ fontSize: '18px', fontWeight: 700 }}>{fmt(produktowa)} zl</div>
        </div>
        <div style={statCard('var(--primary)')}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>RAZEM</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--primary)' }}>{fmt(razem)} zl</div>
        </div>
      </div>

      {/* Info line */}
      {(dniDeklarowane > 0 || progEfektywny > 0) && (
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '10px', padding: '0 4px' }}>
          Przepracowane dni: <strong style={{ color: 'var(--text)' }}>{dniPrzepracowane}</strong> / {dniDeklarowane} deklarowanych
          {progEfektywny > 0 && (
            <span> | Prog efektywny: <strong style={{ color: 'var(--text)' }}>{fmt(progEfektywny)} zl</strong></span>
          )}
        </div>
      )}

      {/* Progress to next threshold */}
      {nextThreshold > 0 && (
        <div style={{ ...card, padding: '10px 12px' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>
            Do nastepnego progu ({fmt(nextThreshold)} zl &rarr; premia {fmt(nextThresholdBonus)} zl)
          </div>
          <ProgressBar value={sprzedaz} max={nextThreshold} height={10} showLabel />
        </div>
      )}

      {/* ── Detailed sales table ──────────────────────────────── */}
      <div style={card}>
        <h5 style={sectionTitle}>Szczegoly sprzedazy</h5>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
          <select
            style={{ ...selectStyle, width: 'auto', minWidth: '160px' }}
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
          >
            <option value="">Wszystkie produkty</option>
            {productNames.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <select
            style={{ ...selectStyle, width: 'auto', minWidth: '160px' }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Wszystkie</option>
            <option value="problems">Tylko z problemami</option>
          </select>
          <input
            style={{ ...inputStyle, maxWidth: '200px' }}
            placeholder="Szukaj produktu lub paragonu..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {filteredItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)' }}>
            {allItems.length === 0
              ? 'Brak danych sprzedazy w tym miesiacu.'
              : 'Brak wynikow dla wybranych filtrow.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={th}>Data</th>
                  <th style={th}>Paragon</th>
                  <th style={th}>Produkt</th>
                  <th style={{ ...th, textAlign: 'right' }}>Ilosc</th>
                  <th style={{ ...th, textAlign: 'right' }}>Cena sp.</th>
                  <th style={{ ...th, textAlign: 'right' }}>Marza %</th>
                  <th style={{ ...th, textAlign: 'right' }}>Premia</th>
                  <th style={{ ...th, textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item, i) => {
                  const isWarning = item.premia_naliczona === false;
                  const rowBg = isWarning ? 'rgba(255,193,7,0.06)' : 'transparent';
                  return (
                    <tr
                      key={i}
                      style={{
                        backgroundColor: rowBg,
                        cursor: isWarning ? 'pointer' : 'default',
                        transition: 'background 0.15s'
                      }}
                      onClick={isWarning ? () => setExplanationItem(item) : undefined}
                      onMouseEnter={(e) => { if (isWarning) e.currentTarget.style.backgroundColor = 'rgba(255,193,7,0.12)'; }}
                      onMouseLeave={(e) => { if (isWarning) e.currentTarget.style.backgroundColor = rowBg; }}
                    >
                      <td style={td}>{item.data_sprzedazy || '-'}</td>
                      <td style={td}>{item.numer_paragonu || '-'}</td>
                      <td style={td}>{item.nazwa_produktu || '-'}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{item.ilosc || 1}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{fmt(item.cena_sprzedazy)} zl</td>
                      <td style={{ ...td, textAlign: 'right' }}>{Number(item.marza_procent || 0).toFixed(1)}%</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>
                        {item.premia_naliczona !== false ? `${fmt(item.premia_kwota)} zl` : '-'}
                      </td>
                      <td style={{ ...td, textAlign: 'center', fontSize: '16px' }}>
                        {isWarning ? (
                          <span title="Premia nie naliczona - kliknij aby dowiedziec sie wiecej" style={{ color: 'var(--warning)', cursor: 'pointer' }}>&#9888;&#65039;</span>
                        ) : (
                          <span title="Premia naliczona" style={{ color: 'var(--success)' }}>&#9989;</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Summary per product ────────────────────────────────── */}
      {productSummary.length > 0 && (
        <div style={card}>
          <h5 style={sectionTitle}>Podsumowanie wg produktow</h5>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={th}>Produkt</th>
                  <th style={{ ...th, textAlign: 'right' }}>Sprzedane</th>
                  <th style={{ ...th, textAlign: 'right' }}>OK</th>
                  <th style={{ ...th, textAlign: 'right' }}>Pominiete</th>
                  <th style={{ ...th, textAlign: 'right' }}>Sr. marza</th>
                  <th style={{ ...th, textAlign: 'right' }}>Premia</th>
                </tr>
              </thead>
              <tbody>
                {productSummary.map((p, i) => (
                  <tr key={i}>
                    <td style={td}>{p.nazwa}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{p.sprzedane}</td>
                    <td style={{ ...td, textAlign: 'right', color: 'var(--success)' }}>{p.ok}</td>
                    <td style={{ ...td, textAlign: 'right', color: p.pominiete > 0 ? 'var(--warning)' : 'var(--text-muted)' }}>
                      {p.pominiete}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {p.countMarza > 0 ? (p.sumMarza / p.countMarza).toFixed(1) + '%' : '-'}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(p.premia)} zl</td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <td style={{ ...td, fontWeight: 700 }}>RAZEM</td>
                  <td style={{ ...td, textAlign: 'right' }}>{totalSummary.sprzedane}</td>
                  <td style={{ ...td, textAlign: 'right', color: 'var(--success)' }}>{totalSummary.ok}</td>
                  <td style={{ ...td, textAlign: 'right', color: totalSummary.pominiete > 0 ? 'var(--warning)' : 'var(--text-muted)' }}>
                    {totalSummary.pominiete}
                  </td>
                  <td style={td} />
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: 'var(--primary)' }}>{fmt(totalSummary.premia)} zl</td>
                </tr>
              </tbody>
            </table>
          </div>

          {totalSummary.pominiete > 0 && (
            <div style={{ fontSize: '11px', color: 'var(--warning)', marginTop: '8px', padding: '0 4px' }}>
              &#9888;&#65039; {totalSummary.pominiete} sztuk pominietych &mdash; kliknij wiersz w tabeli powyzej aby zobaczyc dlaczego
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <ExplanationModal item={explanationItem} onClose={() => setExplanationItem(null)} />
      <BonusTutorial isOpen={tutorialOpen} onClose={() => setTutorialOpen(false)} locationId={locationId} />
    </div>
  );
}
